"use client";

import * as React from "react";
import InputMask from "react-input-mask";
import { CalendarIcon } from "@radix-ui/react-icons";
import { format, parse } from "date-fns";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

export function MonthInputPicker({
  date,
  onSelect,
  className = "",
  inputClassName = "",
  disabledDate = undefined,
  futureYear = "",
  handleDateInputChange,
}) {
  const [open, setOpen] = React.useState(false);

  const [inputValue, setInputValue] = React.useState(
    date ? format(date, "MM-yyyy") : "",
  );

  const [selectedYear, setSelectedYear] = React.useState(
    date ? date.getFullYear() : new Date().getFullYear(),
  );

  React.useEffect(() => {
    if (date) {
      setInputValue(format(date, "MM-yyyy"));
      setSelectedYear(date.getFullYear());
    } else {
      setInputValue("");
    }
  }, [date]);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const getYearRange = () => {
    const currentYear = new Date().getFullYear();

    return {
      from: 1900,
      to:
        futureYear ||
        currentYear + 10,
    };
  };

  const isMonthDisabled = (monthDate) => {
    if (!disabledDate) {
      return false;
    }

    if (typeof disabledDate === "function") {
      return disabledDate(monthDate);
    }

    if (disabledDate?.after) {
      /*
       * Compare month instead of exact date.
       *
       * Example:
       * disabledDate = { after: 10-Aug-2026 }
       *
       * August 2026 is allowed because it is
       * the same month.
       */
      const selectedMonth = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        1,
      );

      const disabledMonth = new Date(
        disabledDate.after.getFullYear(),
        disabledDate.after.getMonth(),
        1,
      );

      return selectedMonth > disabledMonth;
    }

    if (disabledDate?.before) {
      const selectedMonth = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        1,
      );

      const disabledMonth = new Date(
        disabledDate.before.getFullYear(),
        disabledDate.before.getMonth(),
        1,
      );

      return selectedMonth < disabledMonth;
    }

    return false;
  };

  const handleMonthSelect = (monthIndex) => {
    const monthDate = new Date(
      selectedYear,
      monthIndex,
      1,
    );

    if (isMonthDisabled(monthDate)) {
      return;
    }

    onSelect?.(format(monthDate, "MM-yyyy"));

    handleDateInputChange?.(monthDate);

    setInputValue(
      format(monthDate, "MM-yyyy"),
    );

    setOpen(false);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (
      !inputValue ||
      inputValue.includes("_")
    ) {
      handleDateInputChange?.("");
      return;
    }

    const parsed = parse(
      inputValue,
      "MM-yyyy",
      new Date(),
    );

    if (isNaN(parsed)) {
      setInputValue(
        date
          ? format(date, "MM-yyyy")
          : "",
      );

      handleDateInputChange?.(date || "");

      return;
    }

    const monthDate = new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      1,
    );

    if (isMonthDisabled(monthDate)) {
      setInputValue(
        date
          ? format(date, "MM-yyyy")
          : "",
      );

      handleDateInputChange?.(
        date || "",
      );

      return;
    }

    onSelect?.(format(monthDate, "MM-yyyy"));

    handleDateInputChange?.(
      monthDate,
    );

    setInputValue(
      format(monthDate, "MM-yyyy"),
    );

    setSelectedYear(
      monthDate.getFullYear(),
    );
  };

  const handlePreviousYear = () => {
    const { from } = getYearRange();

    setSelectedYear((prev) =>
      Math.max(prev - 1, from),
    );
  };

  const handleNextYear = () => {
    const { to } = getYearRange();

    setSelectedYear((prev) =>
      Math.min(prev + 1, to),
    );
  };

  return (
    <div
      className={cn(
        "relative",
        className,
      )}
    >
      {/* Month Input */}
      <InputMask
        mask="99-9999"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
      >
        {(inputProps) => (
          <Input
            {...inputProps}
            placeholder="mm-yyyy"
            className={cn(
              "h-9 w-[125px]",
              inputClassName,
            )}
          />
        )}
      </InputMask>

      {/* Calendar / Month Picker */}
      <Popover
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() =>
              setOpen(!open)
            }
            className="absolute top-0 right-0 focus-visible:ring-webPrimary dark:focus-visible:ring-ring hover:text-webPrimary text-webPrimary border-l rounded-ss-none rounded-es-none h-9"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0"
          align="center"
        >
          <div className="w-[280px] p-3">
            {/* Year Header */}
            <div className="flex items-center justify-between mb-4">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={
                  handlePreviousYear
                }
                disabled={
                  selectedYear <=
                  getYearRange().from
                }
              >
                ‹
              </Button>

              <span className="text-sm font-medium">
                {selectedYear}
              </span>

              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={
                  handleNextYear
                }
                disabled={
                  selectedYear >=
                  getYearRange().to
                }
              >
                ›
              </Button>
            </div>

            {/* Month Grid */}
            <div className="grid grid-cols-3 gap-2">
              {months.map(
                (month, index) => {
                  const monthDate =
                    new Date(
                      selectedYear,
                      index,
                      1,
                    );

                  const isSelected =
                    date &&
                    date.getFullYear() ===
                      selectedYear &&
                    date.getMonth() ===
                      index;

                  const isDisabled =
                    isMonthDisabled(
                      monthDate,
                    );

                  return (
                    <Button
                      key={month}
                      type="button"
                      variant="ghost"
                      disabled={
                        isDisabled
                      }
                      onClick={() =>
                        handleMonthSelect(
                          index,
                        )
                      }
                      className={cn(
                        "h-9 text-sm",
                        isSelected &&
                          "bg-webPrimary text-white hover:bg-webPrimary hover:text-white",
                      )}
                    >
                      {month}
                    </Button>
                  );
                },
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
